import {
  AGE_BAND_OPTIONS,
  CAPABILITY_LEVELS,
  DATE_FORMATS,
  IMPORT_FIELDS,
  IMPORT_FIELD_LABELS,
  dedupeKey,
  detectDateFormat,
  detectProfile,
  guessColumnMapping,
  importTemplateCsv,
  matchProcedureValues,
  planImport,
  planProfileImport,
  planTotals,
  procedureLabel,
  PROCEDURES_ALPHABETICAL,
} from '@iculogbook/shared';
import type { ProfileImportPlan } from '@iculogbook/shared';
import type {
  AgeBand,
  CapabilityLevel,
  DateFormat,
  ImportField,
  ImportPlan,
  ImportProfile,
  ProcedureInput,
  ValueMatch,
} from '@iculogbook/shared';
import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { uuidv7 } from 'uuidv7';

import { useAuth } from '../auth/AuthProvider.tsx';
import { Button, Card, Field, Select, useToast } from '../components/index.ts';
import { proceduresRepository } from '../db/repositories/procedures.ts';
import { applyProfileImport, applyProfileImports } from '../features/data/importWriter.ts';
import { WorkbookError, readWorkbook, sheetToRows } from '../features/data/xlsx.ts';

/**
 * The import wizard, for a trainee arriving with a spreadsheet.
 *
 * Five steps, and the trainee confirms each one. Matching is deterministic
 * string comparison in `packages/shared/src/import.ts` — no AI, no network, no
 * service — so it works on a train and gives the same answer twice. The point
 * of the wizard is that every guess it makes is visible and correctable before
 * anything is written.
 */

type Step = 'upload' | 'sheets' | 'profile' | 'columns' | 'values' | 'preview';

/** One tab of a workbook, with what this app made of it. */
type SheetChoice = {
  name: string;
  headers: string[];
  rows: Record<string, string>[];
  profile: ImportProfile | null;
};

const DATE_SAMPLE = new Intl.DateTimeFormat('en-GB', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

export function ImportRoute() {
  const { user } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const fileInput = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState<Step>('upload');
  const [filename, setFilename] = useState('');
  const [rows, setRows] = useState<Record<string, string>[]>([]);
  const [headers, setHeaders] = useState<string[]>([]);
  const [mapping, setMapping] = useState<Partial<Record<ImportField, string>>>({});
  const [dateFormat, setDateFormat] = useState<DateFormat>('DD/MM/YYYY');
  const [matches, setMatches] = useState<ValueMatch[]>([]);
  const [procedureMap, setProcedureMap] = useState<Record<string, string | null>>({});
  const [defaultAgeBand, setDefaultAgeBand] = useState<AgeBand>('51_70');
  const [defaultLevel, setDefaultLevel] = useState<CapabilityLevel>(2);
  const [busy, setBusy] = useState(false);
  /** Set when the sheet was recognised, which skips the mapping steps entirely. */
  const [profile, setProfile] = useState<ImportProfile | null>(null);
  /** Set when an Excel workbook was opened, rather than a single CSV. */
  const [sheets, setSheets] = useState<SheetChoice[]>([]);

  async function readFile(file: File | undefined): Promise<void> {
    if (file === undefined) return;

    setBusy(true);
    try {
      /*
       * An Excel workbook goes down its own path. Saving each tab as a CSV
       * first is the step that loses people — Excel warns that it can only
       * keep one sheet, which reads as an error, and a five-tab export means
       * doing it five times.
       */
      if (/\.xlsx$/i.test(file.name)) {
        const workbook = await readWorkbook(file);

        const choices: SheetChoice[] = workbook.map((sheet) => {
          const { headers, rows: sheetRows } = sheetToRows(sheet);
          return { name: sheet.name, headers, rows: sheetRows, profile: detectProfile(headers) };
        });

        setFilename(file.name);
        setSheets(choices);

        const dated = choices
          .flatMap((choice) => choice.rows)
          .map((row) => row['Date'] ?? '')
          .slice(0, 200);
        setDateFormat(detectDateFormat(dated));

        setStep('sheets');
        return;
      }

      // Loaded on demand: a parser is not worth carrying in the app shell for
      // the one time in a career someone imports a spreadsheet.
      const { default: Papa } = await import('papaparse');
      const text = await file.text();

      const parsed = Papa.parse<Record<string, string>>(text, {
        header: true,
        skipEmptyLines: 'greedy',
        transformHeader: (header) => header.trim(),
      });

      const data = parsed.data.filter((row) =>
        Object.values(row).some((value) => (value ?? '').trim() !== ''),
      );

      if (data.length === 0) {
        showToast({ message: 'That file has no rows in it.', tone: 'danger' });
        return;
      }

      const columns = parsed.meta.fields?.map((field) => field.trim()) ?? [];
      const guessed = guessColumnMapping(columns);

      setFilename(file.name);
      setRows(data);
      setHeaders(columns);
      setMapping(guessed);

      /*
       * A recognised sheet needs no column mapping at all. The Excel logbook's
       * ICU patients sheet is 49 columns wide and one row is an admission plus
       * a dozen procedures — no amount of "which column is the date" gets
       * there, and asking for 49 choices by hand is not an import.
       */
      const found = detectProfile(columns);
      setProfile(found);

      if (found === null) {
        setDateFormat(
          detectDateFormat(data.map((row) => row[guessed.date ?? ''] ?? '').slice(0, 200)),
        );
        setStep('columns');
      } else {
        const dateColumn = columns.find((column) => column.trim().toLowerCase() === 'date') ?? '';
        setDateFormat(detectDateFormat(data.map((row) => row[dateColumn] ?? '').slice(0, 200)));
        setStep('profile');
      }
    } catch (error) {
      showToast({
        message:
          error instanceof WorkbookError
            ? error.message
            : 'That file could not be read. Save it as CSV or Excel and try again.',
        tone: 'danger',
      });
    } finally {
      setBusy(false);
      if (fileInput.current !== null) fileInput.current.value = '';
    }
  }

  function toValues(): void {
    const found = matchProcedureValues(rows.map((row) => row[mapping.procedure ?? ''] ?? ''));

    setMatches(found);
    setProcedureMap(
      Object.fromEntries(
        found
          .filter((match) => match.procedureKey !== null)
          .map((match) => [match.value, match.procedureKey]),
      ),
    );
    setStep('values');
  }

  const profilePlan = useMemo(
    () =>
      profile === null
        ? null
        : planProfileImport(profile, rows, {
            dateFormat,
            defaultAgeBand,
            defaultCapabilityLevel: defaultLevel,
          }),
    [profile, rows, dateFormat, defaultAgeBand, defaultLevel],
  );

  const sheetPlans = useMemo(
    () =>
      sheets.map((sheet) => ({
        sheet,
        plan:
          sheet.profile === null
            ? null
            : planProfileImport(sheet.profile, sheet.rows, {
                dateFormat,
                defaultAgeBand,
                defaultCapabilityLevel: defaultLevel,
              }),
      })),
    [sheets, dateFormat, defaultAgeBand, defaultLevel],
  );

  async function commitAllSheets(): Promise<void> {
    if (user === null) return;

    const plans = sheetPlans
      .map((entry) => entry.plan)
      .filter((plan): plan is ProfileImportPlan => plan !== null && planTotals(plan).total > 0);

    if (plans.length === 0) return;

    setBusy(true);
    try {
      const outcome = await applyProfileImports(user.id, plans);

      showToast({
        message: `Imported ${String(outcome.total)} ${outcome.total === 1 ? 'entry' : 'entries'} from ${String(plans.length)} ${plans.length === 1 ? 'sheet' : 'sheets'}.`,
        tone: 'success',
      });
      void navigate('/entries');
    } finally {
      setBusy(false);
    }
  }

  async function commitProfile(): Promise<void> {
    if (user === null || profilePlan === null) return;

    setBusy(true);
    try {
      const outcome = await applyProfileImport(user.id, profilePlan);

      showToast({
        message: `Imported ${String(outcome.total)} ${outcome.total === 1 ? 'entry' : 'entries'}.`,
        tone: 'success',
      });
      void navigate('/entries');
    } finally {
      setBusy(false);
    }
  }

  const plan: ImportPlan = useMemo(
    () =>
      planImport(rows, {
        mapping,
        dateFormat,
        procedureMap,
        defaultAgeBand,
        defaultCapabilityLevel: defaultLevel,
      }),
    [rows, mapping, dateFormat, procedureMap, defaultAgeBand, defaultLevel],
  );

  async function commit(): Promise<void> {
    if (user === null) return;

    setBusy(true);
    try {
      // Deduplicated against what is already here, so importing a spreadsheet
      // twice after adding a few lines brings in only the new lines.
      const existing = await proceduresRepository.list(user.id);
      const seen = new Set(
        existing.map((record) =>
          dedupeKey({ occurredAt: record.occurredAt, procedureKey: record.procedureKey }),
        ),
      );

      const inputs: ProcedureInput[] = [];
      let duplicates = 0;

      for (const row of plan.rows) {
        const key = dedupeKey({ occurredAt: row.occurredAt, procedureKey: row.procedureKey });
        if (seen.has(key)) {
          duplicates += 1;
          continue;
        }

        seen.add(key);

        // `index` and `sourceProcedure` exist so the preview can name a row and
        // quote what the file said. Neither belongs in the stored entry.
        const input = { ...row } as Partial<typeof row>;
        delete input.index;
        delete input.sourceProcedure;
        inputs.push(input as ProcedureInput);
      }

      const written = await proceduresRepository.importBatch(user.id, inputs, uuidv7());

      showToast({
        message: `Imported ${String(written)} ${written === 1 ? 'entry' : 'entries'}${duplicates === 0 ? '' : `, skipped ${String(duplicates)} already here`}.`,
        tone: 'success',
      });
      void navigate('/entries');
    } finally {
      setBusy(false);
    }
  }

  if (step === 'upload') {
    return (
      <Card title="Import from a spreadsheet">
        <p className="text-sm text-muted">
          Bring a logbook you already keep. Nothing is written until you have seen exactly what will
          be imported, and an import can be undone in one tap afterwards.
        </p>

        <p className="pt-3 text-sm text-muted">
          Choose an Excel workbook (<span className="font-medium text-text">.xlsx</span>) and every
          sheet in it is read at once — you do not need to save each tab out separately. A single{' '}
          <span className="font-medium text-text">.csv</span> works too. Everything happens on this
          device; the file is never uploaded anywhere.
        </p>

        <input
          ref={fileInput}
          type="file"
          accept=".xlsx,.csv,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          className="sr-only"
          onChange={(event) => void readFile(event.target.files?.[0])}
        />

        <div className="flex flex-wrap gap-2 pt-4">
          <Button variant="primary" loading={busy} onClick={() => fileInput.current?.click()}>
            Choose a spreadsheet
          </Button>
          <Button
            variant="ghost"
            onClick={() => {
              const blob = new Blob([importTemplateCsv()], { type: 'text/csv' });
              const url = URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              link.download = 'ICULogbook_template.csv';
              link.click();
              setTimeout(() => URL.revokeObjectURL(url), 1000);
            }}
          >
            Download a template
          </Button>
        </div>
      </Card>
    );
  }

  if (step === 'sheets') {
    const recognised = sheetPlans.filter(
      (entry) => entry.plan !== null && planTotals(entry.plan).total > 0,
    );
    const grandTotal = recognised.reduce(
      (sum, entry) => sum + (entry.plan === null ? 0 : planTotals(entry.plan).total),
      0,
    );

    return (
      <Card title="Import from a spreadsheet">
        <p className="text-sm text-muted">
          <span className="font-medium text-text">{filename}</span> has{' '}
          {String(sheets.length)} {sheets.length === 1 ? 'sheet' : 'sheets'}. Everything happens on
          this device; the file is never uploaded.
        </p>

        <ul className="mt-4 flex flex-col gap-2">
          {sheetPlans.map((entry) => {
            const totals = entry.plan === null ? null : planTotals(entry.plan);
            const usable = totals !== null && totals.total > 0;

            return (
              <li
                key={entry.sheet.name}
                className="flex items-center justify-between gap-3 rounded-md border border-line bg-surface px-4 py-3"
              >
                <span className="flex min-w-0 flex-col">
                  <span className="truncate text-sm font-medium text-text">
                    {entry.sheet.name}
                  </span>
                  <span className="text-xs text-faint">
                    {entry.sheet.profile === null
                      ? 'Not recognised — nothing to import'
                      : totals === null || totals.total === 0
                        ? `${entry.sheet.profile.label} — no rows to import`
                        : `${entry.sheet.profile.label} — ${String(totals.total)} ${totals.total === 1 ? 'entry' : 'entries'}`}
                  </span>
                </span>

                {!usable ? null : (
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setProfile(entry.sheet.profile);
                      setRows(entry.sheet.rows);
                      setHeaders(entry.sheet.headers);
                      setStep('profile');
                    }}
                  >
                    Review
                  </Button>
                )}
              </li>
            );
          })}
        </ul>

        {recognised.length === 0 ? (
          <p className="pt-4 text-sm text-warning">
            None of these sheets looks like a logbook this app recognises. You can still import one
            by mapping its columns yourself — choose a CSV instead.
          </p>
        ) : null}

        <div className="flex flex-wrap gap-2 pt-4">
          <Button
            variant="primary"
            loading={busy}
            disabled={recognised.length === 0}
            onClick={() => void commitAllSheets()}
          >
            Import all {String(grandTotal)} {grandTotal === 1 ? 'entry' : 'entries'}
          </Button>
          <Button
            variant="ghost"
            onClick={() => {
              setSheets([]);
              setStep('upload');
            }}
          >
            Choose another file
          </Button>
        </div>

        <p className="pt-3 text-xs text-faint">
          Importing everything counts as one import, so Undo takes all of it back at once.
          {recognised.length === sheets.length
            ? ''
            : ' Sheets that are not recognised are left alone.'}
        </p>
      </Card>
    );
  }

  if (step === 'profile' && profile !== null && profilePlan !== null) {
    const totals = planTotals(profilePlan);
    const rowsRead = rows.length;

    return (
      <Card title="Import from a spreadsheet">
        <p className="text-sm text-text">
          This looks like <span className="font-medium">{profile.label}</span> from the{' '}
          <span className="font-medium">{profile.source}</span>.
        </p>
        <p className="pt-1 text-sm text-muted">{profile.description}</p>

        <div className="mt-4 rounded-md border border-line bg-surface p-4">
          <p className="text-sm font-medium text-text">
            {totals.total === 0
              ? 'Nothing to import from this sheet.'
              : `${String(totals.total)} ${totals.total === 1 ? 'entry' : 'entries'} from ${String(rowsRead)} ${rowsRead === 1 ? 'row' : 'rows'}`}
          </p>
          <ul className="pt-2 text-sm text-muted">
            {totals.cases === 0 ? null : <li>{String(totals.cases)} cases</li>}
            {totals.procedures === 0 ? null : <li>{String(totals.procedures)} procedures</li>}
            {totals.edtEntries === 0 ? null : (
              <li>{String(totals.edtEntries)} EDT / Teaching entries</li>
            )}
            {totals.anaestheticCases === 0 ? null : (
              <li>
                {String(totals.anaestheticCases)} anaesthetic cases —{' '}
                <span className="text-faint">counted separately from your ICM work</span>
              </li>
            )}
          </ul>
        </div>

        {/*
          Named, not silently dropped. A trainee has to be able to see that a
          column was left behind and why — especially this set, which is the
          reason this app can promise it holds nothing identifying a patient.
        */}
        {profilePlan.refused.length === 0 ? null : (
          <div className="mt-3 rounded-md border border-line bg-surface-sunken p-4">
            <p className="text-sm font-medium text-text">
              {profilePlan.refused.length} {profilePlan.refused.length === 1 ? 'column' : 'columns'}{' '}
              will not be imported
            </p>
            <ul className="pt-2 text-xs text-muted">
              {profilePlan.refused.map((column) => (
                <li key={column.header} className="pt-1">
                  <span className="font-medium text-text">{column.header}</span> — {column.why}
                </li>
              ))}
            </ul>
          </div>
        )}

        {profilePlan.skipped.length === 0 ? null : (
          <div className="mt-3 rounded-md border border-line p-4">
            <p className="text-sm text-warning">
              {String(profilePlan.skipped.length)}{' '}
              {profilePlan.skipped.length === 1 ? 'row' : 'rows'} will be skipped
            </p>
            <ul className="pt-2 text-xs text-muted">
              {[...new Set(profilePlan.skipped.map((row) => row.reason))].map((reason) => (
                <li key={reason} className="pt-1">
                  {reason} ×
                  {String(profilePlan.skipped.filter((row) => row.reason === reason).length)}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="mt-4 flex flex-col gap-4">
          <Field label="Dates in this file are">
            <Select
              options={DATE_FORMATS.map((format) => ({ value: format, label: format }))}
              value={dateFormat}
              onChange={(event) => setDateFormat(event.target.value as DateFormat)}
            />
          </Field>

          <Field
            label="Age band where the file does not say"
            help="This sheet may not record an age for every row."
          >
            <Select
              options={AGE_BAND_OPTIONS.map((option) => ({
                value: option.key,
                label: option.label,
              }))}
              value={defaultAgeBand}
              onChange={(event) => setDefaultAgeBand(event.target.value as AgeBand)}
            />
          </Field>

          <Field
            label="Capability level where the file does not say"
            help="Supervision words are read as capability levels: immediate is 1, local is 2, distant or solo is 3, and supervising someone else is 4."
          >
            <Select
              options={CAPABILITY_LEVELS.map((level) => ({
                value: String(level),
                label: `Level ${String(level)}`,
              }))}
              value={String(defaultLevel)}
              onChange={(event) => setDefaultLevel(Number(event.target.value) as CapabilityLevel)}
            />
          </Field>
        </div>

        <p className="pt-4 text-xs text-faint">
          Times recorded as a band become a representative time: morning 10:30, afternoon 15:30,
          evening 20:00 and night 03:00. Evening and night count as out of hours on your summary.
        </p>

        <div className="flex flex-wrap gap-2 pt-4">
          <Button
            variant="primary"
            loading={busy}
            disabled={totals.total === 0}
            onClick={() => void commitProfile()}
          >
            Import {String(totals.total)} {totals.total === 1 ? 'entry' : 'entries'}
          </Button>
          <Button variant="ghost" onClick={() => setStep('columns')}>
            Map the columns myself
          </Button>
          <Button
            variant="ghost"
            onClick={() => {
              setProfile(null);
              setStep('upload');
            }}
          >
            Choose another file
          </Button>
        </div>
      </Card>
    );
  }

  if (step === 'columns') {
    return (
      <Card title={`Which column is which — ${filename}`}>
        <p className="text-sm text-muted">
          {String(rows.length)} rows, {String(headers.length)} columns. We have guessed the
          following; correct anything that is wrong.
        </p>

        <div className="flex flex-col gap-3 pt-4">
          {IMPORT_FIELDS.map((field) => (
            <Field key={field} label={IMPORT_FIELD_LABELS[field]}>
              <Select
                placeholder="— not in my file —"
                value={mapping[field] ?? ''}
                options={headers.map((header) => ({ value: header, label: header }))}
                onChange={(event) =>
                  setMapping((current) => ({
                    ...current,
                    [field]: event.target.value === '' ? undefined : event.target.value,
                  }))
                }
              />
            </Field>
          ))}

          <Field
            label="Date format"
            help="Detected from your file. Getting this wrong moves entries by months, so check it."
          >
            <Select
              value={dateFormat}
              options={DATE_FORMATS.map((format) => ({ value: format, label: format }))}
              onChange={(event) => setDateFormat(event.target.value as DateFormat)}
            />
          </Field>
        </div>

        <div className="flex gap-2 pt-4">
          <Button variant="primary" disabled={mapping.procedure === undefined} onClick={toValues}>
            Next
          </Button>
          <Button variant="ghost" onClick={() => setStep('upload')}>
            Back
          </Button>
        </div>

        {mapping.procedure !== undefined ? null : (
          <p className="pt-2 text-xs text-danger">
            Choose which column holds the procedure name — without it there is nothing to import.
          </p>
        )}
      </Card>
    );
  }

  if (step === 'values') {
    const confident = matches.filter((match) => match.confident);
    const uncertain = matches.filter((match) => !match.confident);

    return (
      <Card title="Which procedure is which">
        <p className="text-sm text-muted">
          {String(confident.length)} of {String(matches.length)} names matched exactly. Check the
          rest — anything left unmatched is skipped and reported, never guessed at.
        </p>

        <div className="flex flex-col gap-3 pt-4">
          {[...uncertain, ...confident].map((match) => (
            <Field
              key={match.value}
              label={match.value}
              help={match.confident ? 'Matched exactly' : 'Please check'}
            >
              <Select
                placeholder="— skip these rows —"
                value={procedureMap[match.value] ?? ''}
                options={PROCEDURES_ALPHABETICAL.map((procedure) => ({
                  value: procedure.key,
                  label: procedure.label,
                }))}
                onChange={(event) =>
                  setProcedureMap((current) => ({
                    ...current,
                    [match.value]: event.target.value === '' ? null : event.target.value,
                  }))
                }
              />
            </Field>
          ))}
        </div>

        <div className="mt-5 flex flex-col gap-3 border-t border-line pt-4">
          <p className="text-sm text-muted">
            Used where a row does not say. Both are required on every entry, so an import cannot
            leave them empty.
          </p>

          <Field label="Age band when not given">
            <Select
              value={defaultAgeBand}
              options={AGE_BAND_OPTIONS.map((option) => ({
                value: option.key,
                label: option.label,
              }))}
              onChange={(event) => setDefaultAgeBand(event.target.value as AgeBand)}
            />
          </Field>

          <Field label="Capability level when not given">
            <Select
              value={String(defaultLevel)}
              options={CAPABILITY_LEVELS.map((level) => ({
                value: String(level),
                label: `Level ${String(level)}`,
              }))}
              onChange={(event) => setDefaultLevel(Number(event.target.value) as CapabilityLevel)}
            />
          </Field>
        </div>

        <div className="flex gap-2 pt-4">
          <Button variant="primary" onClick={() => setStep('preview')}>
            Next
          </Button>
          <Button variant="ghost" onClick={() => setStep('columns')}>
            Back
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <Card title="Check before importing">
      <div className="flex flex-wrap gap-4 text-sm">
        <span className="text-text">{String(rows.length)} rows in the file</span>
        <span className="text-success">{String(plan.rows.length)} will be imported</span>
        {plan.skipped.length === 0 ? null : (
          <span className="text-warning">{String(plan.skipped.length)} will be skipped</span>
        )}
      </div>

      <ul className="flex flex-col gap-2 pt-4">
        {plan.rows.slice(0, 10).map((row) => (
          <li key={row.index} className="flex flex-col border-b border-line pb-2 text-sm">
            <span className="text-text">{procedureLabel(row.procedureKey)}</span>
            <span className="text-xs text-faint">
              {DATE_SAMPLE.format(row.occurredAt)} · Level {String(row.capabilityLevel)} ·{' '}
              {row.ageBand.replace('_', '–')}
              {row.variant === undefined ? '' : ` · ${row.variant}`}
            </span>
          </li>
        ))}
      </ul>

      {plan.skipped.length === 0 ? null : (
        <div className="mt-4 rounded-md border border-line bg-surface-raised p-3">
          <h3 className="text-sm font-medium text-text">Skipped rows</h3>
          <ul className="flex flex-col gap-1 pt-2 text-xs text-muted">
            {plan.skipped.slice(0, 8).map((row) => (
              <li key={row.index}>
                Row {String(row.index + 2)}: {row.reason}
                {row.value === '' ? '' : ` — “${row.value}”`}
              </li>
            ))}
            {plan.skipped.length > 8 ? <li>and {String(plan.skipped.length - 8)} more</li> : null}
          </ul>
        </div>
      )}

      <div className="flex gap-2 pt-4">
        <Button
          variant="primary"
          loading={busy}
          disabled={plan.rows.length === 0}
          onClick={() => void commit()}
        >
          Import {String(plan.rows.length)} entries
        </Button>
        <Button variant="ghost" onClick={() => setStep('values')}>
          Back
        </Button>
      </div>
    </Card>
  );
}
